import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { filter, isEmpty, map, size, sumBy, uniq } from 'es-toolkit/compat';
import { TokenTrace } from 'interfaces/agentSession';
import React from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';

const formatNumber = (n: number): string => n.toLocaleString();

const formatTraceTime = (timestamp: number): string =>
  timestamp > 0 ? formatDateTimeForUser(timestamp * 1000, { timeStyle: 'medium' }) : '-';

const getUniqueTraceValues = (values: Array<string | null | undefined>): string[] =>
  uniq(filter(values, (value): value is string => Boolean(value)));

const getTraceSpanLabel = (trace: TokenTrace): string => {
  if (trace.entries.length === 0) {
    return '-';
  }

  const firstTimestamp = trace.entries[0].timestamp;
  const lastTimestamp = trace.entries[trace.entries.length - 1].timestamp;

  if (firstTimestamp === lastTimestamp) {
    return formatTraceTime(firstTimestamp);
  }

  return `${formatTraceTime(firstTimestamp)} to ${formatTraceTime(lastTimestamp)}`;
};

const TokenSummaryChips: React.FC<{ trace: TokenTrace }> = ({ trace }) => (
  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
    <Chip size="small" label={`In: ${formatNumber(trace.totalInputTokens)}`} color="info" variant="outlined" />
    <Chip size="small" label={`Out: ${formatNumber(trace.totalOutputTokens)}`} color="success" variant="outlined" />
    <Chip size="small" label={`Total: ${formatNumber(trace.totalTokens)}`} variant="outlined" />
    {trace.totalCacheReadTokens > 0 && (
      <Chip size="small" label={`Cache read: ${formatNumber(trace.totalCacheReadTokens)}`} variant="outlined" />
    )}
    {trace.totalCacheWriteTokens > 0 && (
      <Chip size="small" label={`Cache write: ${formatNumber(trace.totalCacheWriteTokens)}`} variant="outlined" />
    )}
  </Stack>
);

const TraceHighlights: React.FC<{
  trace: TokenTrace;
  modelCalls: TokenTrace['entries'];
  toolCalls: TokenTrace['entries'];
}> = ({ trace, modelCalls, toolCalls }) => {
  const agents = getUniqueTraceValues(map(modelCalls, (entry) => entry.agent));
  const tools = getUniqueTraceValues(map(toolCalls, (entry) => entry.toolName));

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        <Chip size="small" label={`Model calls: ${modelCalls.length}`} variant="outlined" />
        <Chip size="small" label={`Tool calls: ${toolCalls.length}`} variant="outlined" />
        {toolCalls.length > 0 && (
          <Chip
            size="small"
            label={`Tool time: ${formatNumber(sumBy(toolCalls, 'durationMs'))}ms`}
            variant="outlined"
          />
        )}
        <Chip size="small" label={`Trace span: ${getTraceSpanLabel(trace)}`} variant="outlined" />
      </Stack>
      {agents.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Agents
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {map(agents, (agentName) => (
              <Chip key={agentName} size="small" label={agentName} sx={{ height: 22 }} />
            ))}
          </Stack>
        </Box>
      )}
      {tools.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Tools
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
            {map(tools, (toolName) => (
              <Chip key={toolName} size="small" label={toolName} sx={{ height: 22 }} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

const TraceTable: React.FC<{ trace: TokenTrace }> = ({ trace }) => {
  const modelCalls = filter(trace.entries, (entry) => entry.type === 'model_call');
  const toolCalls = filter(trace.entries, (entry) => entry.type === 'tool_call');

  return (
    <Stack spacing={1.5}>
      <TokenSummaryChips trace={trace} />
      <TraceHighlights trace={trace} modelCalls={modelCalls} toolCalls={toolCalls} />

      {trace.planText && (
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Plan
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              maxHeight: 200,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: '0.75rem',
              fontFamily: 'monospace',
            }}
          >
            {trace.planText}
          </Box>
        </Box>
      )}

      {modelCalls.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Model calls ({modelCalls.length})
          </Typography>
          <TableContainer sx={{ mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Time</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Agent</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">
                    In
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">
                    Out
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">
                    Total
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">
                    Cache R
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">
                    Cache W
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(modelCalls, (entry) => (
                  <TableRow key={entry.step}>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>{entry.step}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>{formatTraceTime(entry.timestamp)}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>{entry.agent}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }} align="right">
                      {formatNumber(entry.inputTokens)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }} align="right">
                      {formatNumber(entry.outputTokens)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }} align="right">
                      {formatNumber(entry.totalTokens)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }} align="right">
                      {entry.cacheReadTokens > 0 ? formatNumber(entry.cacheReadTokens) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }} align="right">
                      {entry.cacheWriteTokens > 0 ? formatNumber(entry.cacheWriteTokens) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {toolCalls.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Tool calls ({toolCalls.length}) — Total: {formatNumber(sumBy(toolCalls, 'durationMs'))}ms
          </Typography>
          <TableContainer sx={{ mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Time</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Agent</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>Tool</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }} align="right">
                    Duration
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(toolCalls, (entry) => (
                  <TableRow key={entry.step}>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>{entry.step}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>{formatTraceTime(entry.timestamp)}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>{entry.agent}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }}>{entry.toolName || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', py: 0.25 }} align="right">
                      {formatNumber(entry.durationMs)}ms
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Stack>
  );
};

interface TokenTracePopupProps {
  canViewTokenUsageDebug?: boolean;
  tokenTraces: TokenTrace[];
}

export const TokenTracePopup: React.FC<TokenTracePopupProps> = ({ tokenTraces, canViewTokenUsageDebug = false }) => {
  const [open, setOpen] = React.useState(false);

  if (!canViewTokenUsageDebug || isEmpty(tokenTraces)) return null;

  const totalInput = sumBy(tokenTraces, 'totalInputTokens');
  const totalOutput = sumBy(tokenTraces, 'totalOutputTokens');
  const totalTokens = sumBy(tokenTraces, 'totalTokens');
  const totalCacheRead = sumBy(tokenTraces, 'totalCacheReadTokens');
  const totalCacheWrite = sumBy(tokenTraces, 'totalCacheWriteTokens');

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={`Token usage: ${formatNumber(totalTokens)} total (${tokenTraces.length} turn(s))`}>
          <IconButton
            size="small"
            aria-label="Open token usage debug"
            onClick={() => setOpen(true)}
            sx={(theme) => ({
              width: theme.spacing(3.5),
              height: theme.spacing(3.5),
              p: 0.625,
              borderRadius: theme.shape.borderRadius,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'action.hover',
              color: 'text.secondary',
              opacity: 0.82,
              transition: theme.transitions.create(
                ['background-color', 'border-color', 'color', 'opacity', 'transform'],
                {
                  duration: theme.transitions.duration.shortest,
                }
              ),
              '&:hover': {
                bgcolor: 'action.selected',
                color: 'text.primary',
                opacity: 1,
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': {
                bgcolor: 'action.selected',
                color: 'text.primary',
                opacity: 1,
              },
            })}
          >
            <BugReportRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontSize="1rem">
              Token Usage Debug
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Chip size="small" label={`In: ${formatNumber(totalInput)}`} color="info" variant="outlined" />
              <Chip size="small" label={`Out: ${formatNumber(totalOutput)}`} color="success" variant="outlined" />
              <Chip size="small" label={`Total: ${formatNumber(totalTokens)}`} variant="filled" />
              {totalCacheRead > 0 && (
                <Chip size="small" label={`Cache read: ${formatNumber(totalCacheRead)}`} variant="outlined" />
              )}
              {totalCacheWrite > 0 && (
                <Chip size="small" label={`Cache write: ${formatNumber(totalCacheWrite)}`} variant="outlined" />
              )}
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {map(tokenTraces, (trace, index) => (
            <Accordion key={index} defaultExpanded={tokenTraces.length === 1}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    Turn {index + 1}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${formatNumber(trace.totalTokens)} tokens`}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Chip
                    size="small"
                    label={`${size(filter(trace.entries, (entry) => entry.type === 'model_call'))} model calls`}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Chip
                    size="small"
                    label={`${size(filter(trace.entries, (entry) => entry.type === 'tool_call'))} tool calls`}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <TraceTable trace={trace} />
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};
